import React from 'react';
import { Table, Tag, Tooltip, Progress, Typography, theme, Space } from 'antd';
import {
  TrophyOutlined,
  CrownOutlined,
  UserAddOutlined,
  DollarOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

interface TopCampaign {
  id: number;
  name: string;
  push_type: string;
  push_count: number;
  sent: number;
  clicked: number;
  ctr: number;
  registrations: number;
  deposits: number;
  conversion_rate: number;
}

interface TopCampaignsTableProps {
  campaigns: TopCampaign[];
}

const pushTypeTranslations: Record<string, string> = {
  welcome: 'Приветственный',
  promo: 'Промо',
  reminder: 'Напоминание',
  custom: 'Кастомный',
  system: 'Системный',
  marketing: 'Маркетинговый',
  transaction: 'Транзакционный',
};

const conversionTagColor = (rate: number) => {
  if (rate >= 10) return 'success';
  if (rate >= 5) return 'processing';
  return 'default';
};

export const TopCampaignsTable: React.FC<TopCampaignsTableProps> = ({ campaigns }) => {
  const { token } = theme.useToken();

  const ctrColor = (ctr: number) => {
    if (ctr >= 5) return token.colorSuccess;
    if (ctr >= 2) return token.colorPrimary;
    return token.colorTextQuaternary;
  };

  const columns = [
    {
      title: 'Место',
      key: 'rank',
      width: 60,
      fixed: 'left' as const,
      render: (_: unknown, __: TopCampaign, index: number) =>
        index === 0 ? (
          <Text strong style={{ color: token.colorPrimary }}>
            {index + 1}
          </Text>
        ) : (
          <Text type="secondary">{index + 1}</Text>
        ),
    },
    {
      title: 'Кампания',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      fixed: 'left' as const,
      render: (name: string, record: TopCampaign) => (
        <Tooltip title={name}>
          <div>
            <Text strong ellipsis style={{ display: 'block' }}>
              {name}
            </Text>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                ID: {record.id}
              </Text>
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'push_type',
      key: 'push_type',
      width: 130,
      render: (type: string) => <Tag>{pushTypeTranslations[type] || type}</Tag>,
    },
    {
      title: 'Push-ей',
      dataIndex: 'push_count',
      key: 'push_count',
      width: 90,
      render: (count: number) => <Text>{count.toLocaleString('ru-RU')}</Text>,
    },
    {
      title: 'Отправлено',
      dataIndex: 'sent',
      key: 'sent',
      width: 110,
      sorter: (a: TopCampaign, b: TopCampaign) => a.sent - b.sent,
      render: (sent: number) => <Text strong>{sent.toLocaleString('ru-RU')}</Text>,
    },
    {
      title: 'Кликов',
      dataIndex: 'clicked',
      key: 'clicked',
      width: 100,
      sorter: (a: TopCampaign, b: TopCampaign) => a.clicked - b.clicked,
      render: (clicked: number) => <Text strong>{clicked.toLocaleString('ru-RU')}</Text>,
    },
    {
      title: 'CTR',
      dataIndex: 'ctr',
      key: 'ctr',
      width: 120,
      sorter: (a: TopCampaign, b: TopCampaign) => a.ctr - b.ctr,
      render: (ctr: number) => (
        <div>
          <Text strong style={{ fontSize: 12 }}>
            {ctr.toFixed(2)}%
          </Text>
          <Progress
            percent={Math.min(ctr * 10, 100)}
            showInfo={false}
            strokeColor={ctrColor(ctr)}
            size="small"
          />
        </div>
      ),
    },
    {
      title: 'Конверсии',
      key: 'conversions',
      width: 130,
      render: (_: unknown, record: TopCampaign) => (
        <Space size={12}>
          <Tooltip title="Регистрации">
            <Text style={{ fontSize: 12 }}>
              <UserAddOutlined /> {record.registrations}
            </Text>
          </Tooltip>
          <Tooltip title="Депозиты">
            <Text style={{ fontSize: 12 }}>
              <DollarOutlined /> {record.deposits}
            </Text>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Конв. %',
      dataIndex: 'conversion_rate',
      key: 'conversion_rate',
      width: 100,
      sorter: (a: TopCampaign, b: TopCampaign) => a.conversion_rate - b.conversion_rate,
      render: (rate: number) => <Tag color={conversionTagColor(rate)}>{rate.toFixed(2)}%</Tag>,
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={campaigns}
      rowKey="id"
      pagination={false}
      scroll={{ x: 1080 }}
      size="small"
      locale={{ emptyText: 'Нет данных' }}
    />
  );
};